import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaHandsHelping, FaLeaf, FaCheck, FaClock, FaUsers, FaShieldAlt, FaLightbulb } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import swedishImage from '../../components/assets/Staff/Swedish 1.jpg';
import swedish2Image from '../../components/assets/Staff/Swedish 2.jpg';

const SwedishMassage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const techniquesRef = useRef(null);
  const benefitsRef = useRef(null);
  const audienceRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'techniques': techniquesRef,
      'benefits': benefitsRef,
      'audience': audienceRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'techniques', ref: techniquesRef },
        { id: 'benefits', ref: benefitsRef },
        { id: 'audience', ref: audienceRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview', icon: FaHeart },
    { id: 'techniques', label: 'How Your Session Unfolds', icon: FaHandsHelping },
    { id: 'benefits', label: 'What You May Notice', icon: FaLeaf },
    { id: 'audience', label: 'Who This Is For', icon: FaUsers }
  ];

  const overviewFeatures = [
    {
      icon: FaHeart,
      title: "Time-Honoured",
      description: "One of the most well-known and time-honoured forms of massage"
    },
    {
      icon: FaHandsHelping,
      title: "Smooth & Rhythmic",
      description: "Smooth, rhythmic strokes and carefully adapted pressure for relaxation and balance"
    },
    {
      icon: FaLeaf,
      title: "Listening, Not Pushing",
      description: "Not about pushing or fixing — listening to your body, your breath, and what feels right"
    }
  ];

  const massageTechniques = [
    {
      name: "Effleurage",
      description: "Long, gliding strokes that invite relaxation and warmth"
    },
    {
      name: "Petrissage",
      description: "Gentle kneading to soften tight areas and support ease of movement"
    },
    {
      name: "Friction",
      description: "Focused work for areas that feel overworked or held"
    },
    {
      name: "Tapotement",
      description: "Light, rhythmic tapping to gently awaken tired muscles"
    },
    {
      name: "Vibration",
      description: "Subtle shaking to encourage a sense of release and flow"
    }
  ];

  const massageBenefits = [
    "A deeper sense of calm and mental clarity",
    "A feeling of lightness and comfort in the body",
    "Less everyday muscular tightness",
    "Emotional ease and greater presence",
    "Easier rest and improved sleep quality",
    "A renewed connection with their body"
  ];

  const targetAudience = [
    "Feel busy, overwhelmed, or mentally over-stimulated",
    "Spend long hours at a desk or travelling",
    "Notice tension in the neck, shoulders, back, or hips",
    "Are new to massage and want a gentle, reassuring experience",
    "Value regular self-care in a calm, respectful space"
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Swedish Massage" description="Swedish massage at Bioresonance Leicester – traditional relaxation and muscle tension relief. Time-honoured techniques at 108 New Walk." path="/therapy/swedish-massage" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">Swedish Massage</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHandsHelping className="mr-2" />
                Slow down, soften, come back to yourself
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                Swedish Massage
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed">
                Life can feel full — full schedules, full minds, full bodies holding more than they need to. Swedish massage offers a calm, grounded pause in the middle of it all.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                This is a flowing, nurturing massage designed to help you slow down, ease everyday tension, and feel more at home in your body. Each session is guided by presence, attentive touch, and your needs on the day — creating space for your system to settle and your breath to deepen.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Many people leave feeling quieter inside, lighter in the body, and more connected to themselves.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>60-90 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Gentle & Safe</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container - same photo as Swedish Massage card */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={swedishImage}
                      alt="Swedish Massage"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What is Swedish Massage?
              </h2>
              <p className="text-lg text-gray-600">
                Swedish massage is one of the most well-known and time-honoured forms of massage. Using smooth, rhythmic strokes and carefully adapted pressure, it supports relaxation, comfort, and a natural sense of balance.
              </p>
              <p className="text-lg text-gray-600 mt-4">
                This is not about pushing or fixing. It's about listening — to your body, your breath, and what feels right for you in that moment.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                >
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-2xl text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Awareness Disclaimer */}
            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaLightbulb className="text-xl text-gold" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                    More than relaxation — a practice of care
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    This is not a one-size-fits-all experience. Each session is shaped by how you arrive and what you need that day. The focus is on presence, communication, and conscious touch — creating an experience that feels safe, grounding, and deeply supportive.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section ref={techniquesRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                  How your session unfolds
                </h2>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    Your massage weaves together five classic techniques — each chosen to support relaxation, ease, and a natural sense of flow.
                  </p>
                  <p>
                    Pressure is always adjusted to your preference — from light and soothing to deeper and more targeted — and can change at any time during the session.
                  </p>
                </div>
              </div>
              
              <div className="relative">
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-3xl h-80">
                    <img
                      src={swedish2Image}
                      alt="Swedish massage techniques"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-2"></div>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h3 className="text-2xl font-bold text-dark-grey mb-6 text-center">Five classic techniques</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {massageTechniques.map((technique, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-grey/20"
                  >
                    <h4 className="text-lg font-semibold text-dark-grey mb-3">{technique.name}</h4>
                    <p className="text-gray-600 text-sm">{technique.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What It Supports */}
      <section ref={benefitsRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What you may notice
              </h2>
              <p className="text-lg text-gray-600">
                With regular sessions, many clients experience:
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="space-y-4 mb-6">
                  {massageBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-sm" />
                      </div>
                      <p className="text-gray-600">{benefit}</p>
                    </motion.div>
                  ))}
                </div>
                <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                  <p className="text-dark-grey font-semibold text-lg">
                    Clients often describe Swedish massage as "a reset" — a moment to pause, breathe, and come back to themselves.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-dark-grey mb-4">Your experience</h3>
                <p className="text-gray-600 leading-relaxed">
                  Whether received on its own or as part of an ongoing wellbeing routine, Swedish massage offers time, attention, and space — something many of us need more of.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is It For */}
      <section ref={audienceRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaUsers className="mr-2" />
                Who this massage is for
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Who this massage is for
              </h2>
              <p className="text-lg text-gray-600">
                Swedish massage may be especially supportive if you:
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <div className="space-y-3">
                  {targetAudience.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-xs" />
                      </div>
                      <p className="text-gray-600 text-sm">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                  More than relaxation — a practice of care
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  This is not a one-size-fits-all experience. Each session is shaped by how you arrive and what you need that day. The focus is on presence, communication, and conscious touch — creating an experience that feels safe, grounding, and deeply supportive.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Whether received on its own or as part of an ongoing wellbeing routine, Swedish massage offers time, attention, and space — something many of us need more of.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to book your session?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Book online or get in touch if you have any questions — I'm happy to help you decide what feels right for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Book Your Session
            </a>
            <Link
              to="/prices"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default SwedishMassage;
